/**
 * Self-service password reset — token redemption page.
 *
 * Route: /reset-password?token=<jwt>
 *
 * Flow:
 *   1. User clicks the reset link in their email
 *   2. This page loads with ?token=… in the URL
 *   3. User enters a new password (twice)
 *   4. We POST /api/auth/reset-password with {token, new_password}
 *   5. On success → redirect to /login with a success toast
 *
 * Error surfaces:
 *   - expired link         → clear message + button to request a new one
 *   - already-used link    → same, "already used" copy
 *   - invalid / malformed  → generic invalid-link message
 */

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Loader2, ShieldCheck, AlertTriangle } from 'lucide-react';
import { AppShell } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [tokenError, setTokenError] = useState('');

  useEffect(() => {
    if (!token) setTokenError('This reset link is missing its token.');
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords don\u2019t match.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${BACKEND}/api/auth/reset-password`,
        { token, new_password: password },
        { timeout: 15000 },
      );
      setDone(true);
      toast.success('Password updated. You can log in now.');
      setTimeout(() => navigate('/login', { replace: true }), 1400);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Reset failed. Try again.';
      setTokenError(typeof msg === 'string' ? msg : 'Reset failed.');
      toast.error(typeof msg === 'string' ? msg : 'Reset failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell>
      <main className="min-h-[calc(100vh-80px)] px-4 py-12 flex items-start justify-center">
        <Card className="w-full max-w-md border-border">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck size={18} className="text-primary" />
              <h1 className="text-xl font-semibold">Reset password</h1>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              Pick a new password. This link is single-use and expires in 1 hour.
            </p>

            {tokenError && (
              <div
                data-testid="reset-password-error"
                className="mb-5 rounded-lg border border-destructive/30 bg-destructive/[0.06] p-3 flex items-start gap-2 text-sm text-destructive"
              >
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">Couldn&apos;t reset your password</p>
                  <p className="text-[13px] opacity-90 mt-0.5">{tokenError}</p>
                  <Link
                    to="/login"
                    className="mt-2 inline-block text-[13px] font-medium underline underline-offset-4"
                    data-testid="reset-password-back-to-login"
                  >
                    Request a new reset link
                  </Link>
                </div>
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-password-form">
              <div className="space-y-1.5">
                <Label htmlFor="new-password">New password</Label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-3 text-muted-foreground" />
                  <Input
                    id="new-password"
                    type="password"
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    className="pl-9 h-11"
                    disabled={busy || done || !token}
                    data-testid="reset-password-new-input"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-password">Confirm password</Label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-3 text-muted-foreground" />
                  <Input
                    id="confirm-password"
                    type="password"
                    required
                    minLength={6}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repeat the password"
                    className="pl-9 h-11"
                    disabled={busy || done || !token}
                    data-testid="reset-password-confirm-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full h-11 rounded-xl"
                disabled={busy || done || !token}
                data-testid="reset-password-submit"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : (done ? 'Updated \u2014 redirecting\u2026' : 'Update password')}
              </Button>
            </form>

            <p className="text-xs text-muted-foreground mt-6 text-center">
              Back to <Link to="/login" className="text-primary underline underline-offset-4">Login</Link>
            </p>
          </CardContent>
        </Card>
      </main>
    </AppShell>
  );
}
